import { useToast } from '@app/core/notifications';
import { pushTo } from '@app/core/routing';
import {
  CreateSocialUploadDto,
  CreateSocialUploadDtoPlatformType,
  CreateSocialUploadDtoSourceType,
  EnrichedVideoEditorEditListItemDto,
  GetFragmentDto,
  ListEditsVideoEditorEditsQueryResponse,
} from '@app/modules/fragments/fragment.types';
import { useFragmentQuery } from '@app/modules/fragments/hooks/useFragment';
import {
  SOCIAL_EXPORT_FRAGMENT_SOURCE,
  useAvailableSocialExportSources,
} from '@app/modules/fragments/hooks/useAvailableSocialExportSources';
import { useFragmentMediaUrlQuery } from '@app/modules/fragments/hooks/useFragmentMediaUrlQuery';
import { SocialPlatform, socialPlatforms } from '@app/modules/social-export/domain/platforms';
import {
  type BaseExportFormProps,
  useSocialExportFactory,
} from '@app/modules/social-export/hooks/useSocialExportFactory';
import { socialExportPublishMessages } from '@app/modules/social-export/publish';
import { getEditRenderedVideoUrl } from '@app/modules/video-editions';
import { socialExport } from '@mi-front/api-clients';
import {
  useWebAppCreateFragmentSocialUploads,
  webAppGetFragmentSocialUploadsByFragmentIdQueryKey,
  webAppListEditsVideoEditorEditsQueryKey,
} from '@mi-front/api-v2';
import { type UseQueryResult, useQueryClient } from '@tanstack/react-query';
import { ComponentType, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useParams } from 'react-router-dom';

export type SocialExportPublishForm = ComponentType<
  BaseExportFormProps<CreateSocialUploadDto, socialExport.SettingsGetConnection200>
>;

export interface UseSocialExportUploadPageResult {
  fragmentId: string;
  platform: SocialPlatform;
  fragmentQuery: ReturnType<typeof useFragmentQuery>;
  mediaUrlQuery: ReturnType<typeof useFragmentMediaUrlQuery>;
  settingsQuery: ReturnType<
    typeof socialExport.useSettingsGetConnection<socialExport.SettingsGetConnection200>
  >;
  editsListQuery: UseQueryResult<ListEditsVideoEditorEditsQueryResponse, unknown> & {
    queryKey: ReturnType<typeof webAppListEditsVideoEditorEditsQueryKey>;
  };
  selectedSource: string;
  setSelectedSource: (value: string) => void;
  edits: EnrichedVideoEditorEditListItemDto[];
  isFragmentSourceAllowed: boolean;
  hasAvailableSources: boolean;
  showMediaSelector: boolean;
  hasSelectedRenderedEdit: boolean;
  renderedVideoUrl: string | null;
  PublishForm: SocialExportPublishForm;
  handleUpload: (fragment: GetFragmentDto, data: CreateSocialUploadDto) => Promise<void>;
}

/**
 * Owns routing, source selection, connection settings, rendered preview URL, and the
 * create-upload mutation for the social export upload flow.
 */
export function useSocialExportUploadPage(): UseSocialExportUploadPageResult {
  const { fragmentId, platform: platformParam } = useParams<{
    fragmentId: string;
    platform: string;
  }>();
  const platform = socialPlatforms.get(platformParam!.toUpperCase())!;
  const { $t } = useIntl();
  const { notifyInfo, notifyError } = useToast();
  const queryClient = useQueryClient();
  const { search } = useLocation();
  /** Preselected video-editor edit id from the upload URL (`editId` preferred; `projectId` kept for backward compatibility). */
  const searchParams = new URLSearchParams(search);
  const editIdFromUrl = searchParams.get('editId') ?? searchParams.get('projectId') ?? undefined;

  const [selectedSource, setSelectedSource] = useState<string>(
    () => editIdFromUrl ?? SOCIAL_EXPORT_FRAGMENT_SOURCE,
  );

  const fragmentQuery = useFragmentQuery(fragmentId!);
  const mediaUrlQuery = useFragmentMediaUrlQuery(fragmentId!);
  const { editsListQuery, edits, editsWithRender, isFragmentSourceAllowed, hasAvailableSources } =
    useAvailableSocialExportSources({
      fragmentId: fragmentId!,
      stationId: fragmentQuery.data?.stationId,
    });

  const selectedEdit =
    selectedSource !== SOCIAL_EXPORT_FRAGMENT_SOURCE
      ? (edits.find((edit) => edit.id === selectedSource) ?? null)
      : null;

  const { url: renderedVideoUrl } = getEditRenderedVideoUrl(selectedEdit, {
    mode: 'playback',
  });

  const showMediaSelector = editsWithRender.length > 0;
  const hasSelectedRenderedEdit = editsWithRender.some((edit) => edit.id === selectedSource);

  useEffect(() => {
    if (editIdFromUrl) {
      setSelectedSource(editIdFromUrl);
    }
  }, [editIdFromUrl]);

  useEffect(() => {
    if (editIdFromUrl || isFragmentSourceAllowed) return;
    if (selectedSource !== SOCIAL_EXPORT_FRAGMENT_SOURCE) return;

    const firstRenderedEditId = editsWithRender[0]?.id;
    if (!firstRenderedEditId) return;

    setSelectedSource(firstRenderedEditId);
  }, [isFragmentSourceAllowed, editIdFromUrl, editsWithRender, selectedSource]);

  const useExport = useSocialExportFactory(platform);

  const settingsQuery =
    socialExport.useSettingsGetConnection<socialExport.SettingsGetConnection200>(
      fragmentQuery.data?.stationId ?? '',
      platform.id,
      {
        query: {
          enabled: !!fragmentQuery.data?.stationId,
        },
      },
    );

  const uploadMutation = useWebAppCreateFragmentSocialUploads({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: webAppGetFragmentSocialUploadsByFragmentIdQueryKey(fragmentId!),
        });
        notifyInfo($t(socialExportPublishMessages.publicationStarted));
      },
      onError: (error: Error) => {
        notifyError($t(socialExportPublishMessages.startPublicationError));
        console.log(error);
      },
    },
  });

  const handleUpload = async (fragment: GetFragmentDto, data: CreateSocialUploadDto) => {
    try {
      if (selectedSource === SOCIAL_EXPORT_FRAGMENT_SOURCE && !isFragmentSourceAllowed) {
        notifyError($t(socialExportPublishMessages.noAllowedSourceError));
        return;
      }

      const sourceType =
        selectedSource === SOCIAL_EXPORT_FRAGMENT_SOURCE
          ? CreateSocialUploadDtoSourceType.FRAGMENT
          : CreateSocialUploadDtoSourceType.VIDEO_EDITOR_EDIT;
      const sourceId =
        selectedSource === SOCIAL_EXPORT_FRAGMENT_SOURCE ? fragment.id : selectedSource;

      await uploadMutation.mutateAsync({
        data: {
          sourceType,
          sourceId,
          platform: platform.id as CreateSocialUploadDtoPlatformType,
          data,
        },
      });
      pushTo(`/fragments/${fragment.id}/publish`);
    } catch (error: unknown) {
      notifyError((error as Error).message);
    }
  };

  const { PublishForm } = useExport();

  return {
    fragmentId: fragmentId!,
    platform,
    fragmentQuery,
    mediaUrlQuery,
    settingsQuery,
    editsListQuery,
    selectedSource,
    setSelectedSource,
    edits,
    isFragmentSourceAllowed,
    hasAvailableSources,
    showMediaSelector,
    hasSelectedRenderedEdit,
    renderedVideoUrl,
    PublishForm: PublishForm as SocialExportPublishForm,
    handleUpload,
  };
}
